import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Settings, AppSettings } from './app.settings';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from "ngx-cookie-service";
import { MetaTagService } from "./services/meta-tag.service";
import { CompanyService } from "./services/company.service";
import { API_CONFIG } from "./constans/api-config";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  loading: boolean = false;
  showGlobalChatbot = true;
  /** "האתר בהקמה" notice; closing it hides it for the rest of the session. */
  showConstructionBanner = true;
  /** Ribbon that marks every non-production build (DEV / LOCAL / STG ...). */
  readonly envLabel: string = (API_CONFIG as { appEnv?: string }).appEnv || 'dev';
  readonly showEnvRibbon = this.envLabel.toLowerCase() !== 'prod';
  public settings: Settings;
  private cookieValue: string;
  constructor(public appSettings: AppSettings,
              public router: Router,
              private cookieService: CookieService,
              @Inject(PLATFORM_ID) private platformId: Object,
              @Inject(DOCUMENT) private document: Document,
              private metaTagService: MetaTagService,
              private companyService: CompanyService,
              public translate: TranslateService) {

    this.settings = this.appSettings.settings;
    this.showGlobalChatbot = !this.isPublishRoute(this.router.url);
    translate.addLangs(['he']);
    translate.setDefaultLang('he');
    translate.use('he');

    // The private-preview password prompt was removed (2026-10-13): it sent
    // every new visitor — including Meta's WhatsApp business reviewers — to
    // google.com, which is why Meta reported the website "couldn't be found".
    // Original code: _claude_backup_20261013_index/app.component.ts
    try {
      this.showConstructionBanner = sessionStorage.getItem('s4_construction_banner') !== 'closed';
    } catch {
      this.showConstructionBanner = true;
    }
    this.document.body.classList.toggle('s4-banner-on', this.showConstructionBanner);

  }

  hideConstructionBanner(): void {
    this.showConstructionBanner = false;
    this.document.body.classList.remove('s4-banner-on');
    try {
      sessionStorage.setItem('s4_construction_banner', 'closed');
    } catch {
      /* private mode / blocked storage: hidden until reload */
    }
  }

  ngOnInit(): void {
    // Load SEO settings from the company profile API (microservice-driven SEO)
    this.companyService.getCompanyInfo().subscribe({
      next: (company) => {
        const siteUrl = (company.seo_site_url || '').replace(/\/$/, '') || 'https://sale4all.co.il';

        // Resolve OG image: Django returns a relative /media/... path
        let ogImage = company.seo_og_image || '';
        if (ogImage && !ogImage.startsWith('http')) {
          ogImage = `${siteUrl}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;
        }

        // 1. Seed the service with live API values — all per-page seo.update()
        //    calls that happen after navigation will automatically use these.
        this.metaTagService.init({
          siteName:      company.name               || undefined,
          siteUrl:       siteUrl,
          defaultImg:    ogImage                    || undefined,
          defaultDesc:   company.seo_description   || undefined,
          defaultKw:     company.seo_keywords       || undefined,
          twitterHandle: company.seo_twitter_handle || undefined,
        });

        // 2. Apply the global (homepage-level) tags immediately.
        this.metaTagService.update({
          title:       company.seo_title || company.name,
          description: company.seo_description   || undefined,
          keywords:    company.seo_keywords       || undefined,
          image:       ogImage                    || undefined,
          url:         siteUrl,
        });

        // 3. Swap the browser favicon if one is set in the admin.
        if (company.favicon) {
          this._applyFavicon(company.favicon, siteUrl);
        }
      },
      error: () => {
        // API unavailable — the service already has the hardcoded fallback constants,
        // so just trigger update() with no args to apply them.
        this.metaTagService.update();
      }
    });
  }

  private _applyFavicon(faviconPath: string, siteUrl: string): void {
    // Resolve relative Django media paths to absolute URLs
    const href = faviconPath.startsWith('http')
      ? faviconPath
      : `${siteUrl}${faviconPath.startsWith('/') ? '' : '/'}${faviconPath}`;

    // Detect type from extension
    const ext  = href.split('.').pop()?.toLowerCase() || 'ico';
    const type = ext === 'svg' ? 'image/svg+xml'
               : ext === 'png' ? 'image/png'
               : 'image/x-icon';

    // Update every existing favicon link tag, or create one if missing
    const links = this.document.querySelectorAll<HTMLLinkElement>('link[rel*="icon"]');
    if (links.length > 0) {
      links.forEach(link => {
        link.setAttribute('href', href);
        link.setAttribute('type', type);
      });
    } else {
      const link = this.document.createElement('link');
      link.setAttribute('rel',  'icon');
      link.setAttribute('type', type);
      link.setAttribute('href', href);
      this.document.head.appendChild(link);
    }
  }

  ngAfterViewInit(){
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.showGlobalChatbot = !this.isPublishRoute(event.urlAfterRedirects);
        if (isPlatformBrowser(this.platformId)) {
          window.scrollTo(0,0);
        } 
      }
    })  
  }

  private isPublishRoute(url: string): boolean {
    const path = (url || '').split('?')[0].split('#')[0];
    return path === '/publish' || path.startsWith('/publish/');
  }
}
